import { useMemo } from 'react';
import { WorkItem } from '../types';
import { GroupedWorkItem } from '../types/table';

export const useTableData = (
  data: WorkItem[],
  categories: string[],
  viewMode: 'tree' | 'table',
  globalFilter: string,
  refreshTrigger?: number
) => {
  const groupedData = useMemo(() => {
    const grouped: Record<string, Record<string, WorkItem[]>> = {};
    
    // Initialize all categories as empty
    categories.forEach(category => {
      if (!grouped[category]) {
        grouped[category] = { 'Items': [] };
      }
    });
    
    // Load saved subcategories from localStorage
    const savedSubcategories = localStorage.getItem('cleanRoomSubcategories');
    if (savedSubcategories) {
      try {
        const subcategoriesMap = JSON.parse(savedSubcategories);
        Object.entries(subcategoriesMap).forEach(([category, subcategories]) => {
          if (Array.isArray(subcategories) && grouped[category]) {
            subcategories.forEach((subcategory: string) => {
              if (!grouped[category][subcategory]) {
                grouped[category][subcategory] = [];
              }
            });
          }
        });
      } catch (e) {
        console.error('Failed to parse saved subcategories');
      }
    }
    
    // Add actual data
    data.forEach(item => {
      if (!grouped[item.level1]) {
        grouped[item.level1] = {};
      }
      if (!grouped[item.level1][item.level2]) {
        grouped[item.level1][item.level2] = [];
      }
      grouped[item.level1][item.level2].push(item);
    });

    const result: GroupedWorkItem[] = [];
    const categoryIndexMap: Record<string, number> = {};
    
    // Get all unique categories from both saved categories and actual data
    const allCategories = new Set([...categories]);
    Object.keys(grouped).forEach(cat => allCategories.add(cat));
    const finalCategories = Array.from(allCategories);
    
    // Process all categories
    finalCategories.forEach((level1, index) => {
      const level2Data = grouped[level1];
      if (!level2Data) return;
      
      categoryIndexMap[level1] = index;
      
      const level1Item: GroupedWorkItem = {
        id: `level1-${level1}`,
        level1,
        level2: '',
        fileName: '',
        link: '',
        description: '',
        comments: [],
        tags: [],
        progress: 0,
        isGroupHeader: true,
        level: 1,
        categoryIndex: categoryIndexMap[level1],
        subRows: []
      };

      // Sort level2 entries to put "Items" first if it exists
      const sortedLevel2Entries = Object.entries(level2Data).sort(([a], [b]) => {
        if (a === 'Items') return -1;
        if (b === 'Items') return 1;
        return a.localeCompare(b);
      });

      sortedLevel2Entries.forEach(([level2, items]) => {
        // If level2 is "Items" (default), add items directly to level1
        if (level2 === 'Items') {
          level1Item.subRows!.push(...items.map(item => ({ ...item, level: 3 })));
        } else {
          // Create subcategory group (show even if empty since user created it)
          const level2Item: GroupedWorkItem = {
            id: `level2::${level1}::${level2}`,
            level1,
            level2,
            fileName: '',
            link: '',
            description: '',
            comments: [],
            tags: [],
            progress: 0,
            isGroupHeader: true,
            level: 2,
            subRows: items.map(item => ({ ...item, level: 3 }))
          };
          level1Item.subRows!.push(level2Item);
        }
      });

      result.push(level1Item);
    });

    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, categories]); // refreshTrigger intentionally excluded

  // When searching, use flat data to show only items
  const tableData = useMemo(() => {
    if (globalFilter && viewMode === 'tree') {
      // In search mode, show only items (no categories)
      return data;
    }
    return viewMode === 'table' ? data : groupedData;
  }, [viewMode, globalFilter, data, groupedData]);

  return { tableData, groupedData };
};